/**
 * Accounting systems we support
 */
export var ACCOUNTING_SYSTEMS;
(function (ACCOUNTING_SYSTEMS) {
    ACCOUNTING_SYSTEMS["XERO"] = "XERO";
    ACCOUNTING_SYSTEMS["QBO"] = "QBO";
    ACCOUNTING_SYSTEMS["INTERNAL"] = "INTERNAL";
})(ACCOUNTING_SYSTEMS || (ACCOUNTING_SYSTEMS = {}));
/**
 * Accounting system specific terms and UI configurations
 */
export var ACCOUNTING_CONFIG;
(function (ACCOUNTING_CONFIG) {
    ACCOUNTING_CONFIG["HIDE_CONTACT_GROUPS"] = "HIDE_CONTACT_GROUPS";
    ACCOUNTING_CONFIG["HIDE_INVOICE_STATUS"] = "HIDE_INVOICE_STATUS";
    ACCOUNTING_CONFIG["HIDE_INVOICE_BRANDING"] = "HIDE_INVOICE_BRANDING";
    ACCOUNTING_CONFIG["REQUIRE_TAX_RATE"] = "REQUIRE_TAX_RATE";
    ACCOUNTING_CONFIG["DEFAULT_TAX_RATE"] = "DEFAULT_TAX_RATE";
    /** Tax inclusive / exclusive choice applies to fees (QBO US is handled as a region override) */
    ACCOUNTING_CONFIG["TAX_INCLUSIVE_APPLICABLE"] = "TAX_INCLUSIVE_APPLICABLE";
    /** Organisation timezone comes from the accounting system, so it is not editable in Paidnice */
    ACCOUNTING_CONFIG["TIMEZONE_FROM_ACCOUNTING_SYSTEM"] = "TIMEZONE_FROM_ACCOUNTING_SYSTEM";
    ACCOUNTING_CONFIG["SUPPORTS_MARK_AS_SENT"] = "SUPPORTS_MARK_AS_SENT";
    ACCOUNTING_CONFIG["SUPPORTS_PREPAYMENTS"] = "SUPPORTS_PREPAYMENTS";
    ACCOUNTING_CONFIG["SUPPORTS_ACTIVITY_STATEMENT"] = "SUPPORTS_ACTIVITY_STATEMENT";
    ACCOUNTING_CONFIG["SUPPORTS_DIRECT_STATEMENT_LINK"] = "SUPPORTS_DIRECT_STATEMENT_LINK";
    ACCOUNTING_CONFIG["SUPPORTS_ALTERNATIVE_INVOICE_PDF"] = "SUPPORTS_ALTERNATIVE_INVOICE_PDF";
    ACCOUNTING_CONFIG["SUPPORTS_MANUAL_PARENT_CHILD"] = "SUPPORTS_MANUAL_PARENT_CHILD";
    ACCOUNTING_CONFIG["SUPPORTS_LOGO_IMPORT"] = "SUPPORTS_LOGO_IMPORT";
    ACCOUNTING_CONFIG["SUPPORTS_CUSTOM_FIELDS"] = "SUPPORTS_CUSTOM_FIELDS";
    ACCOUNTING_CONFIG["SUPPORTS_PROJECTS_JOBS"] = "SUPPORTS_PROJECTS_JOBS";
    ACCOUNTING_CONFIG["SUPPORTS_INVOICE_PRIVATE_NOTE"] = "SUPPORTS_INVOICE_PRIVATE_NOTE";
    ACCOUNTING_CONFIG["SUPPORTS_STRIPE_FEE_TAX"] = "SUPPORTS_STRIPE_FEE_TAX";
    /** Custom invoice numbers replace the accounting system's own sequence */
    ACCOUNTING_CONFIG["CUSTOM_INVOICE_NUMBER_UPDATES_SEQUENCE"] = "CUSTOM_INVOICE_NUMBER_UPDATES_SEQUENCE";
    /** Contact persons beyond the primary can be added */
    ACCOUNTING_CONFIG["SUPPORTS_ADDITIONAL_CONTACT_PERSONS"] = "SUPPORTS_ADDITIONAL_CONTACT_PERSONS";
    /** Additional contact persons can be synced back to the accounting system */
    ACCOUNTING_CONFIG["SUPPORTS_SYNCED_CONTACT_PERSONS"] = "SUPPORTS_SYNCED_CONTACT_PERSONS";
    /** Syncing note attachments needs an extra scope granted on reconnect */
    ACCOUNTING_CONFIG["ATTACHMENT_SYNC_NEEDS_RECONNECT"] = "ATTACHMENT_SYNC_NEEDS_RECONNECT";
    /** Email invoice links always go to the Paidnice portal instead of the accounting system's online invoice */
    ACCOUNTING_CONFIG["INVOICE_LINK_USES_PORTAL"] = "INVOICE_LINK_USES_PORTAL";
    /** Phone numbers keep a separate country code (QuickBooks only holds a free form number) */
    ACCOUNTING_CONFIG["SUPPORTS_PHONE_COUNTRY_CODE"] = "SUPPORTS_PHONE_COUNTRY_CODE";
    /** New orgs default the statement reference column to truncate */
    ACCOUNTING_CONFIG["TRUNCATE_STATEMENT_REFERENCE_BY_DEFAULT"] = "TRUNCATE_STATEMENT_REFERENCE_BY_DEFAULT";
    /** Only AUTHORISED credit notes count towards balances */
    ACCOUNTING_CONFIG["CREDIT_NOTES_REQUIRE_AUTHORISED_STATUS"] = "CREDIT_NOTES_REQUIRE_AUTHORISED_STATUS";
    ACCOUNTING_CONFIG["SUPPORTS_OVERPAYMENTS"] = "SUPPORTS_OVERPAYMENTS";
    ACCOUNTING_CONFIG["SUPPORTS_UNAPPLIED_PAYMENTS"] = "SUPPORTS_UNAPPLIED_PAYMENTS";
})(ACCOUNTING_CONFIG || (ACCOUNTING_CONFIG = {}));
export const accountingSystemConfig = {
    'QBO': {
        provider: {
            displayName: 'QuickBooks',
            connectSlug: 'quickbooks',
            brandColor: '#2BA01B',
            customPaymentLinkPlaceholder: 'https://example.com/pay?amount={{{DueAmount}}}&inv={{{InvoiceNumber}}}&company={{{QBOCompanyID}}}',
            maxImportedContactPersons: null,
            deepLinks: {
                invoice: null,
                contact: null,
                quote: null,
            },
        },
        settings: {
            [ACCOUNTING_CONFIG.HIDE_CONTACT_GROUPS]: true,
            [ACCOUNTING_CONFIG.HIDE_INVOICE_STATUS]: true,
            [ACCOUNTING_CONFIG.HIDE_INVOICE_BRANDING]: true,
            [ACCOUNTING_CONFIG.REQUIRE_TAX_RATE]: false,
            [ACCOUNTING_CONFIG.DEFAULT_TAX_RATE]: {
                label: `Item Default Rate`,
                value: null,
            },
            [ACCOUNTING_CONFIG.TAX_INCLUSIVE_APPLICABLE]: true,
            [ACCOUNTING_CONFIG.TIMEZONE_FROM_ACCOUNTING_SYSTEM]: false,
            [ACCOUNTING_CONFIG.SUPPORTS_MARK_AS_SENT]: false,
            [ACCOUNTING_CONFIG.SUPPORTS_PREPAYMENTS]: false,
            [ACCOUNTING_CONFIG.SUPPORTS_ACTIVITY_STATEMENT]: false,
            [ACCOUNTING_CONFIG.SUPPORTS_DIRECT_STATEMENT_LINK]: true,
            [ACCOUNTING_CONFIG.SUPPORTS_ALTERNATIVE_INVOICE_PDF]: false,
            [ACCOUNTING_CONFIG.SUPPORTS_MANUAL_PARENT_CHILD]: false,
            [ACCOUNTING_CONFIG.SUPPORTS_LOGO_IMPORT]: false,
            [ACCOUNTING_CONFIG.SUPPORTS_CUSTOM_FIELDS]: true,
            [ACCOUNTING_CONFIG.SUPPORTS_PROJECTS_JOBS]: true,
            [ACCOUNTING_CONFIG.SUPPORTS_INVOICE_PRIVATE_NOTE]: true,
            [ACCOUNTING_CONFIG.SUPPORTS_STRIPE_FEE_TAX]: true,
            [ACCOUNTING_CONFIG.CUSTOM_INVOICE_NUMBER_UPDATES_SEQUENCE]: true,
            [ACCOUNTING_CONFIG.SUPPORTS_ADDITIONAL_CONTACT_PERSONS]: true,
            [ACCOUNTING_CONFIG.SUPPORTS_SYNCED_CONTACT_PERSONS]: false,
            [ACCOUNTING_CONFIG.ATTACHMENT_SYNC_NEEDS_RECONNECT]: false,
            [ACCOUNTING_CONFIG.INVOICE_LINK_USES_PORTAL]: true,
            [ACCOUNTING_CONFIG.SUPPORTS_PHONE_COUNTRY_CODE]: false,
            [ACCOUNTING_CONFIG.TRUNCATE_STATEMENT_REFERENCE_BY_DEFAULT]: true,
            [ACCOUNTING_CONFIG.CREDIT_NOTES_REQUIRE_AUTHORISED_STATUS]: false,
            [ACCOUNTING_CONFIG.SUPPORTS_OVERPAYMENTS]: false,
            [ACCOUNTING_CONFIG.SUPPORTS_UNAPPLIED_PAYMENTS]: true,
        },
        terminology: {
            'Fee Income Account': 'Fee item',
            'Discount Account': 'Discount item',
            'contact': 'customer',
            'Contact': 'Customer',
            'Contacts': 'Customers',
            'Add Contact or Group': 'Add Customer',
            [`Quotes`]: 'Estimates',
            [`Quote`]: 'Estimate',
            [`Credit Note`]: 'Credit Memo',
            [`Credit Notes`]: 'Credit Memos',
            [`reference`]: 'memo',
            [`Contact Groups`]: 'Customer Types',
            [`Contact Groups Description`]: 'The customer type in QuickBooks that this contact has assigned.',
            [`Account Number`]: 'QuickBooks Customer ID',
            [`contact group rule`]: `this contact's QuickBooks Customer Type`,
            [`Portal Quotes`]: 'Quotes & Estimates',
        },
        tooltips: {},
    },
    'XERO': {
        provider: {
            displayName: 'Xero',
            connectSlug: 'xero',
            brandColor: '#16B5EA',
            customPaymentLinkPlaceholder: 'https://example.com/pay?amount={{{DueAmount}}}&inv={{{InvoiceNumber}}}&org={{{ShortCode}}}',
            maxImportedContactPersons: 6,
            deepLinks: {
                invoice: ({ shortcode, externalId }) => `https://go.xero.com/organisationlogin/default.aspx?shortcode=${shortcode}&redirecturl=/AccountsReceivable/View.aspx?InvoiceID=${externalId}`,
                contact: ({ shortcode, externalId }) => `https://go.xero.com/organisationlogin/default.aspx?shortcode=${shortcode}&redirecturl=/Contacts/View/${externalId}`,
                // Not a proper deep link as not yet supported in Xero
                quote: ({ shortcode, externalId }) => `https://go.xero.com/app/${shortcode}/quotes/view/${externalId}`,
            },
        },
        settings: {
            [ACCOUNTING_CONFIG.HIDE_CONTACT_GROUPS]: false,
            [ACCOUNTING_CONFIG.HIDE_INVOICE_STATUS]: false,
            [ACCOUNTING_CONFIG.HIDE_INVOICE_BRANDING]: false,
            [ACCOUNTING_CONFIG.REQUIRE_TAX_RATE]: false,
            [ACCOUNTING_CONFIG.DEFAULT_TAX_RATE]: {
                label: `Account Default Rate`,
                value: null,
            },
            [ACCOUNTING_CONFIG.TAX_INCLUSIVE_APPLICABLE]: true,
            [ACCOUNTING_CONFIG.TIMEZONE_FROM_ACCOUNTING_SYSTEM]: true,
            [ACCOUNTING_CONFIG.SUPPORTS_MARK_AS_SENT]: true,
            [ACCOUNTING_CONFIG.SUPPORTS_PREPAYMENTS]: true,
            [ACCOUNTING_CONFIG.SUPPORTS_ACTIVITY_STATEMENT]: true,
            [ACCOUNTING_CONFIG.SUPPORTS_DIRECT_STATEMENT_LINK]: true,
            [ACCOUNTING_CONFIG.SUPPORTS_ALTERNATIVE_INVOICE_PDF]: true,
            [ACCOUNTING_CONFIG.SUPPORTS_MANUAL_PARENT_CHILD]: true,
            [ACCOUNTING_CONFIG.SUPPORTS_LOGO_IMPORT]: true,
            [ACCOUNTING_CONFIG.SUPPORTS_CUSTOM_FIELDS]: false,
            [ACCOUNTING_CONFIG.SUPPORTS_PROJECTS_JOBS]: false,
            [ACCOUNTING_CONFIG.SUPPORTS_INVOICE_PRIVATE_NOTE]: false,
            [ACCOUNTING_CONFIG.SUPPORTS_STRIPE_FEE_TAX]: false,
            [ACCOUNTING_CONFIG.CUSTOM_INVOICE_NUMBER_UPDATES_SEQUENCE]: false,
            [ACCOUNTING_CONFIG.SUPPORTS_ADDITIONAL_CONTACT_PERSONS]: true,
            [ACCOUNTING_CONFIG.SUPPORTS_SYNCED_CONTACT_PERSONS]: true,
            [ACCOUNTING_CONFIG.ATTACHMENT_SYNC_NEEDS_RECONNECT]: true,
            [ACCOUNTING_CONFIG.INVOICE_LINK_USES_PORTAL]: false,
            [ACCOUNTING_CONFIG.SUPPORTS_PHONE_COUNTRY_CODE]: true,
            [ACCOUNTING_CONFIG.TRUNCATE_STATEMENT_REFERENCE_BY_DEFAULT]: false,
            [ACCOUNTING_CONFIG.CREDIT_NOTES_REQUIRE_AUTHORISED_STATUS]: true,
            [ACCOUNTING_CONFIG.SUPPORTS_OVERPAYMENTS]: true,
            [ACCOUNTING_CONFIG.SUPPORTS_UNAPPLIED_PAYMENTS]: false,
        },
        terminology: {
            'Fee Income Account': 'Fee Income Account',
            'Discount Account': 'Discount Account',
            'contact': 'contact',
            'Contact': 'Contact',
            'Contacts': 'Contacts',
            'Add Contact or Group': 'Add Contact or Group',
            [`Quotes`]: 'Quotes',
            [`Quote`]: 'Quote',
            [`Credit Note`]: 'Credit Note',
            [`Credit Notes`]: 'Credit Notes',
            [`reference`]: 'reference',
            [`Contact Groups`]: 'Xero Group(s)',
            [`Contact Groups Description`]: 'The contact groups in Xero that this contact is in. Note: A contact can only appear in one Paidnice group.',
            [`Account Number`]: 'Xero Account Number',
            [`contact group rule`]: `any of this contact's Xero Contact Groups`,
            [`Portal Quotes`]: 'Quotes',
        },
        tooltips: {
            [`tax_rate`]: `(Optional) If not selected, or default, then the default tax rate for your account will be used.`,
        },
    },
    'INTERNAL': {
        provider: {
            displayName: 'Internal',
            connectSlug: null,
            brandColor: '#6B7280',
            customPaymentLinkPlaceholder: 'https://example.com/pay?amount={{{DueAmount}}}&inv={{{InvoiceNumber}}}',
            maxImportedContactPersons: null,
            deepLinks: {
                invoice: null,
                contact: null,
                quote: null,
            },
        },
        settings: {
            [ACCOUNTING_CONFIG.HIDE_CONTACT_GROUPS]: true,
            [ACCOUNTING_CONFIG.HIDE_INVOICE_STATUS]: false,
            [ACCOUNTING_CONFIG.HIDE_INVOICE_BRANDING]: true,
            [ACCOUNTING_CONFIG.REQUIRE_TAX_RATE]: false,
            [ACCOUNTING_CONFIG.DEFAULT_TAX_RATE]: {
                label: 'Default Rate',
                value: null,
            },
            [ACCOUNTING_CONFIG.TAX_INCLUSIVE_APPLICABLE]: true,
            [ACCOUNTING_CONFIG.TIMEZONE_FROM_ACCOUNTING_SYSTEM]: false,
            [ACCOUNTING_CONFIG.SUPPORTS_MARK_AS_SENT]: false,
            [ACCOUNTING_CONFIG.SUPPORTS_PREPAYMENTS]: false,
            [ACCOUNTING_CONFIG.SUPPORTS_ACTIVITY_STATEMENT]: false,
            [ACCOUNTING_CONFIG.SUPPORTS_DIRECT_STATEMENT_LINK]: false,
            [ACCOUNTING_CONFIG.SUPPORTS_ALTERNATIVE_INVOICE_PDF]: false,
            [ACCOUNTING_CONFIG.SUPPORTS_MANUAL_PARENT_CHILD]: false,
            [ACCOUNTING_CONFIG.SUPPORTS_LOGO_IMPORT]: false,
            [ACCOUNTING_CONFIG.SUPPORTS_CUSTOM_FIELDS]: false,
            [ACCOUNTING_CONFIG.SUPPORTS_PROJECTS_JOBS]: false,
            [ACCOUNTING_CONFIG.SUPPORTS_INVOICE_PRIVATE_NOTE]: false,
            [ACCOUNTING_CONFIG.SUPPORTS_STRIPE_FEE_TAX]: false,
            [ACCOUNTING_CONFIG.CUSTOM_INVOICE_NUMBER_UPDATES_SEQUENCE]: false,
            [ACCOUNTING_CONFIG.SUPPORTS_ADDITIONAL_CONTACT_PERSONS]: false,
            [ACCOUNTING_CONFIG.SUPPORTS_SYNCED_CONTACT_PERSONS]: false,
            [ACCOUNTING_CONFIG.ATTACHMENT_SYNC_NEEDS_RECONNECT]: false,
            [ACCOUNTING_CONFIG.INVOICE_LINK_USES_PORTAL]: true,
            [ACCOUNTING_CONFIG.SUPPORTS_PHONE_COUNTRY_CODE]: true,
            [ACCOUNTING_CONFIG.TRUNCATE_STATEMENT_REFERENCE_BY_DEFAULT]: false,
            [ACCOUNTING_CONFIG.CREDIT_NOTES_REQUIRE_AUTHORISED_STATUS]: false,
            [ACCOUNTING_CONFIG.SUPPORTS_OVERPAYMENTS]: false,
            [ACCOUNTING_CONFIG.SUPPORTS_UNAPPLIED_PAYMENTS]: false,
        },
        terminology: {
            'Fee Income Account': 'Fee Income Account',
            'Discount Account': 'Discount Account',
            'contact': 'contact',
            'Contact': 'Contact',
            'Contacts': 'Contacts',
            'Add Contact or Group': 'Add Contact or Group',
            [`Quotes`]: 'Quotes',
            [`Quote`]: 'Quote',
            [`Credit Note`]: 'Credit Note',
            [`Credit Notes`]: 'Credit Notes',
            [`reference`]: 'reference',
            [`Contact Groups`]: 'Contact Groups',
            [`Contact Groups Description`]: 'The contact groups this contact is in.',
            [`Account Number`]: 'Account Number',
            [`contact group rule`]: `any of this contact's contact groups`,
            [`Portal Quotes`]: 'Quotes',
        },
        tooltips: {},
    },
};
export function getAccountingSystemsConfigured(config) {
    return Object.values(ACCOUNTING_SYSTEMS).filter(system => {
        var _a;
        return ((_a = accountingSystemConfig[system]) === null || _a === void 0 ? void 0 : _a.settings[config]) || false;
    });
}
/** Accounting systems a user can connect to via OAuth, in display order */
export function getConnectableAccountingSystems() {
    return Object.values(ACCOUNTING_SYSTEMS).filter(system => accountingSystemConfig[system].provider.connectSlug);
}
//# sourceMappingURL=accountingSystems.js.map